import { useQuery } from "react-query";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { fetchRunnables } from "../hooks/useRunnables";
import { Runnable } from "../models/types";
import { getFinalNumberFromPath, getLastPathByRegex } from "../utilities/UIHelper";

/** Map<pathname, state> */
let _pathStateMap: Map<
  string,
  {
    runnableId?: string;
    runnableVersion?: number;
    edit?: boolean;
  }
> = new Map();

const navigateToRunnable = (params: {
  navigate: NavigateFunction;
  runnableId: string;
  version: number;
  runnableName?: string;
  edit?: boolean;
  returnLink?: boolean;
}) => {
  const { navigate, runnableId, runnableName, version, edit, returnLink = false } = params;
  if (returnLink) {
    return `/runnables/${runnableName ?? runnableId}-${version}`;
  }
  const path = `/runnables/${runnableName ?? runnableId}-${version}`;
  _pathStateMap.set(path, {
    runnableId,
    edit,
  });

  navigate(path);
};

const getPathState = (path: string) => {
  return _pathStateMap.get(path) ?? {};
};

const RUNNABLE_NAVIGATOR_KEY = "runnable-navigator";

function getRunnableNameFromPath(path: string): string | null {
  const lastSegment = path.split("/").pop();
  if (!lastSegment) return null;

  // Decode twice to handle %2520 → %20 → space
  let decoded = decodeURIComponent(decodeURIComponent(lastSegment));

  // Match the part before the last dash (assuming version comes after the last dash)
  const match = decoded.match(/^(.*)-[^-]+$/);
  return match ? match[1] : null;
}

const useRunnablePathState = (locationState: any, path: string) => {
  const runnableId = locationState?.runnableId;
  const edit = locationState?.edit;
  const runnableName = getRunnableNameFromPath(path);
  const lastPath = getLastPathByRegex(path);
  const version = getFinalNumberFromPath(lastPath);
  const navigate = useNavigate();
  return useQuery<{
    runnableId: string;
    edit: boolean;
  }>(
    [RUNNABLE_NAVIGATOR_KEY, path],
    async () => {
      if (runnableId != null) {
        return { runnableId, edit };
      }
      if (!runnableName) {
        navigate("/runnables");
        return { runnableId: null, edit };
      }

      const runnables = await fetchRunnables({ "name[]": [runnableName], version: version });
      const runnable = runnables.results.find(
        (t) => t.name === runnableName && `${t.version}` === version
      );
      if (!runnable) {
        navigate("/runnables");
        return { runnableId: null, edit };
      }

      return { runnableId: runnable.id, edit };
    },
    { suspense: true }
  );
};

const navigateToRunnableInNewTab = (runnable: Pick<Runnable, "id" | "name" | "version">) => {
  window.open(`/runnables/${runnable.name}-${runnable.version}`, "_blank");
};

export { navigateToRunnable, navigateToRunnableInNewTab, useRunnablePathState };
